// ============================================================================
// Creator-facing queries.
//
// Every read here is additionally filtered by RLS, so a bug in a .eq() clause
// leaks nothing — the database refuses rows that aren't the caller's. Writes
// go through RPCs (submit_mission, request_redemption) that enforce the rules
// server-side, because anything enforced only in this file is advisory.
// ============================================================================

import { supabase, errorMessage } from '../lib/supabase.js';

/** Everything the dashboard needs, in parallel. */
export async function loadDashboard(creatorId) {
  const [orders, ledger, submissions, tiers] = await Promise.all([
    supabase.from('orders')
      .select('id, order_number, subtotal_cents, commission_cents, commission_rate, status, ordered_at')
      .eq('creator_id', creatorId)
      .order('ordered_at', { ascending: false })
      .limit(50),

    supabase.from('ledger')
      .select('id, entry_type, points_delta, credit_delta_cents, revenue_cents, note, created_at')
      .eq('creator_id', creatorId)
      .order('created_at', { ascending: false })
      .limit(50),

    supabase.from('mission_submissions')
      .select('id, status, points_awarded, submitted_at, reviewed_at, review_note, reviewer_email, mission:missions(title, points, category)')
      .eq('creator_id', creatorId)
      .order('submitted_at', { ascending: false })
      .limit(25),

    supabase.from('tiers').select('*').order('rank')
  ]);

  const firstError = [orders, ledger, submissions, tiers].find((r) => r.error);
  if (firstError) throw new Error(errorMessage(firstError.error, 'Could not load your dashboard.'));

  return {
    orders: orders.data ?? [],
    ledger: ledger.data ?? [],
    submissions: submissions.data ?? [],
    tiers: tiers.data ?? []
  };
}

/**
 * Missions with an eligibility reason attached to each.
 *
 * The reason comes from the same SQL function submit_mission() enforces with,
 * so the grayed-out state in the UI and the actual rule can never drift apart.
 */
export async function loadMissions(creatorId) {
  const { data: missions, error } = await supabase
    .from('missions')
    .select('*')
    .eq('active', true)
    .order('category')
    .order('points', { ascending: false });

  if (error) throw new Error(errorMessage(error, 'Could not load missions.'));
  if (!creatorId) return (missions ?? []).map((m) => ({ ...m, blockedReason: null }));

  const checks = await Promise.all(
    (missions ?? []).map((m) =>
      supabase.rpc('mission_ineligibility_reason', {
        p_creator_id: creatorId,
        p_mission_id: m.id
      })
    )
  );

  return (missions ?? []).map((m, i) => ({
    ...m,
    blockedReason: checks[i].error ? null : (checks[i].data || null)
  }));
}

export async function submitMission({ missionId, proofUrl, proofNote, proofFilePath, disclosureConfirmed }) {
  const { data, error } = await supabase.rpc('submit_mission', {
    p_mission_id: missionId,
    p_proof_url: proofUrl || null,
    p_proof_note: proofNote || null,
    p_proof_file_path: proofFilePath || null,
    p_disclosure_confirmed: !!disclosureConfirmed
  });

  // RPC exceptions are written to be shown verbatim — they say things like
  // "Available again Aug 04", which is exactly what the creator needs.
  if (error) throw new Error(errorMessage(error, 'Could not submit. Try again.'));
  return data;
}

/** Upload proof into the creator's own folder — storage RLS enforces the path. */
export async function uploadProof(creatorId, file) {
  if (!file) throw new Error('Choose a photo first');
  if (file.size > 10 * 1024 * 1024) throw new Error('That image is over 10MB — try a smaller one');
  if (!/^image\//.test(file.type)) throw new Error('That file is not an image');

  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
  const path = `${creatorId}/${crypto.randomUUID()}.${ext}`;

  const { error } = await supabase.storage
    .from('mission-proofs')
    .upload(path, file, { cacheControl: '3600', upsert: false });

  if (error) throw new Error(errorMessage(error, 'Upload failed. Try again.'));
  return path;
}

/**
 * Redeem points for a Shopify gift card.
 *
 * Goes through the redeem-gift-card edge function, not request_redemption
 * directly: the function debits the points AND creates the real gift card,
 * refunding automatically if Shopify fails. It returns the plaintext code
 * once — the caller must show it immediately, because it is never stored and
 * cannot be retrieved again.
 */
export async function redeemPoints(points) {
  const { data, error } = await supabase.functions.invoke('redeem-gift-card', {
    body: { points }
  });

  // Edge-function errors arrive as a FunctionsHttpError whose real message is
  // in the response body, the same unwrap approach as approveCreator.
  if (error) {
    let message = 'Could not redeem those points.';
    try {
      const body = await error.context?.json?.();
      if (body?.error) message = body.error;
    } catch { /* fall back to the generic message */ }
    throw new Error(message);
  }

  return data;   // { ok, code, last_chars, amount_cents }
}

/**
 * Redeem points for a single-use percentage-off coupon (5/10/15/20/25%).
 *
 * Goes through the redeem-coupon edge function, which debits the points, creates
 * a one-of-one code in Shopify, refunds automatically if Shopify fails, and
 * emails the code. Returns the plaintext code once — show it immediately.
 */
export async function redeemCoupon(percent) {
  const { data, error } = await supabase.functions.invoke('redeem-coupon', {
    body: { percent }
  });

  if (error) {
    let message = 'Could not create that coupon.';
    try {
      const body = await error.context?.json?.();
      if (body?.error) message = body.error;
    } catch { /* fall back to the generic message */ }
    throw new Error(message);
  }

  return data;   // { ok, code, percent }
}

/**
 * Nudge the "you can cash out" email. Fire-and-forget on dashboard load: the
 * function is idempotent (sends once, only when over the $50 floor), so calling
 * it on every load is harmless and needs no return value.
 */
export function pokeCashoutEmail() {
  supabase.functions
    .invoke('notify-cashout-ready', { body: {} })
    .catch(() => { /* courtesy email; never surface */ });
}

export async function loadRedemptions(creatorId) {
  const { data, error } = await supabase
    .from('redemptions')
    .select('*')
    .eq('creator_id', creatorId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(errorMessage(error, 'Could not load your redemptions.'));
  return data ?? [];
}

// ----------------------------------------------------------------- drops ----

/**
 * The live drops catalog, each with the reason it cannot be claimed.
 *
 * Same shape and same reasoning as loadMissions: the reason string comes from
 * reward_item_ineligibility_reason(), which is the exact function
 * request_reward_claim() enforces with, so a card can never offer a claim the
 * server is going to refuse.
 *
 * RLS already hides influencer-only drops from ordinary affiliates, so an
 * unlisted item is invisible rather than teasingly grayed out.
 */
export async function loadRewardItems(creatorId) {
  const { data: items, error } = await supabase
    .from('reward_items')
    .select('*')
    .eq('active', true)
    .order('sort')
    .order('point_cost');

  if (error) throw new Error(errorMessage(error, 'Could not load the drops.'));
  if (!creatorId) return (items ?? []).map((i) => ({ ...i, blockedReason: null }));

  const checks = await Promise.all(
    (items ?? []).map((i) =>
      supabase.rpc('reward_item_ineligibility_reason', {
        p_creator_id: creatorId,
        p_item_id: i.id
      })
    )
  );

  return (items ?? []).map((i, idx) => ({
    ...i,
    blockedReason: checks[idx].error ? null : (checks[idx].data || null)
  }));
}

/**
 * Spend points on a physical item.
 *
 * Goes through the claim-reward edge function rather than the RPC directly:
 * the function debits the points AND creates the real Shopify order, unwinding
 * the whole thing (points back, stock back) if Shopify refuses. Calling the RPC
 * straight from here would leave claims that nobody ever ships.
 */
export async function claimReward(itemId) {
  const { data, error } = await supabase.functions.invoke('claim-reward', {
    body: { item_id: itemId }
  });

  if (error) {
    let message = 'Could not claim that item.';
    try {
      const body = await error.context?.json?.();
      if (body?.error) message = body.error;
    } catch { /* fall back to the generic message */ }
    throw new Error(message);
  }

  return data;   // { ok, shipment_id, item_title, points_spent, order_name, address }
}

/**
 * An influencer asking for the product a seeding mission is about.
 *
 * Costs nothing and ships nothing on its own — it queues a request an admin
 * approves, because seeded product is a real cost with no points burned
 * against it. The RPC refuses anyone without the influencer flag.
 */
export async function requestMissionSeed(missionId, note) {
  const { data, error } = await supabase.rpc('request_mission_seed', {
    p_mission_id: missionId,
    p_note: note ? String(note).trim() : null
  });

  if (error) throw new Error(errorMessage(error, 'Could not send that request.'));
  return data;
}

/** Everything physical this creator has coming, newest first. */
export async function loadShipments(creatorId) {
  const { data, error } = await supabase
    .from('shipments')
    .select(`
      id, kind, status, item_title, points_spent, quantity, note, error,
      shopify_order_name, requested_at, approved_at, ordered_at, mission_id,
      ship_name, ship_address1, ship_address2, ship_city, ship_state, ship_zip,
      mission:missions(title)
    `)
    .eq('creator_id', creatorId)
    .order('requested_at', { ascending: false })
    .limit(50);

  if (error) throw new Error(errorMessage(error, 'Could not load your shipments.'));
  return data ?? [];
}

// --------------------------------------------------------------- profile ----

/**
 * Profile fields only — the column guard trigger rejects anything else.
 *
 * The eight ship_* columns are creator-editable by design: your delivery
 * address is yours to change. They are the reason 0019 had to rewrite
 * guard_creator_columns(), which is a deny-list — anything NOT named there is
 * self-editable, so the new influencer flag had to be added to it in the same
 * migration that added these.
 */
export async function updateProfile(creatorId, fields) {
  const text = (v) => {
    const s = String(v ?? '').trim();
    return s === '' ? null : s;
  };

  const allowed = {
    full_name: fields.full_name,
    phone: fields.phone,
    social_platform: fields.social_platform,
    social_handle: fields.social_handle,
    follower_count: parseInt(fields.follower_count, 10) || 0
  };

  // Consent, inverted at the boundary: the form asks a positive question
  // ("send me these") and the column stores the negative, because the default
  // for a column has to be "not opted out" and a double negative in the schema
  // is worse than one here. Only written when the form actually carried the
  // control, so saving from a screen without it never silently re-subscribes.
  if ('email_opt_in' in fields) {
    allowed.email_opt_out = fields.email_opt_in !== 'on' && fields.email_opt_in !== true;
  }

  // Only send the address when the form carried it, so saving from a screen
  // that has no address fields never blanks a saved one.
  if ('ship_address1' in fields) {
    const first = text(fields.ship_first_name);
    const last = text(fields.ship_last_name);

    Object.assign(allowed, {
      ship_first_name: first,
      ship_last_name: last,
      ship_company: text(fields.ship_company),
      // Derived, not typed. Kept in step here so the admin shipping queue and
      // the confirmation emails have one field to show, while Shopify gets the
      // split columns it actually wants.
      ship_name: [first, last].filter(Boolean).join(' ') || null,
      ship_phone: text(fields.ship_phone),
      ship_address1: text(fields.ship_address1),
      ship_address2: text(fields.ship_address2),
      ship_city: text(fields.ship_city),
      ship_state: text(fields.ship_state)?.toUpperCase() ?? null,
      ship_zip: text(fields.ship_zip),
      ship_country: 'US'
    });
  }

  const { error } = await supabase.from('creators').update(allowed).eq('id', creatorId);
  if (error) {
    // The CHECK constraints speak SQL; say it the way someone can act on.
    const message = String(error.message ?? '');
    if (/creators_ship_state_check/.test(message)) {
      throw new Error('State should be the two-letter code, like TX.');
    }
    if (/creators_ship_zip_check/.test(message)) {
      throw new Error('ZIP should be 5 digits, like 78701.');
    }
    throw new Error(errorMessage(error, 'Could not save your profile.'));
  }
}

/**
 * Everything Shopify needs. Company and address2 are genuinely optional.
 * Mirrors creator_can_ship() in 0021 — if these two ever disagree, the button
 * is offered for a claim the database then refuses.
 */
export function shippingComplete(profile) {
  return Boolean(
    profile?.ship_first_name?.trim() &&
    profile?.ship_last_name?.trim() &&
    profile?.ship_address1?.trim() &&
    profile?.ship_city?.trim() &&
    profile?.ship_state?.trim() &&
    profile?.ship_zip?.trim()
  );
}

export async function submitApplication(form) {
  const { data, error } = await supabase.rpc('submit_application', {
    p_full_name: form.name,
    p_email: form.email,
    p_phone: form.phone || null,
    p_social_platform: form.socialPlatform || null,
    p_social_handle: form.socialHandle || null,
    p_follower_count: parseInt(form.followers, 10) || 0,
    p_pitch: form.pitch || null
  });
  if (error) throw new Error(errorMessage(error, 'Could not submit your application.'));
  return data;
}

/** Public — the landing page calculator reads real rates, not hardcoded ones. */
export async function loadTiers() {
  const { data, error } = await supabase.from('tiers').select('*').order('rank');
  if (error) return [];
  return data ?? [];
}

/**
 * Daily revenue for the dashboard chart, bucketed client-side.
 * The row count here is small (a creator's own orders), so this is cheaper
 * than a round trip to an aggregate view.
 */
export function dailyRevenue(orders, days = 30) {
  const buckets = new Map();
  const today = new Date(); today.setHours(0, 0, 0, 0);

  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today); d.setDate(d.getDate() - i);
    buckets.set(d.toISOString().slice(0, 10), { revenue: 0, commission: 0, orders: 0 });
  }

  for (const o of orders) {
    const key = String(o.ordered_at).slice(0, 10);
    const bucket = buckets.get(key);
    if (!bucket) continue;
    bucket.revenue += o.subtotal_cents || 0;
    bucket.commission += o.commission_cents || 0;
    bucket.orders += 1;
  }

  return [...buckets.entries()].map(([date, v]) => ({ date, ...v }));
}

// ----------------------------------------------------------- attendance ----

let visitRecorded = false;

/**
 * Tell the database this creator is here.
 *
 * Fire-and-forget by design. Nothing on screen depends on it, so a failure —
 * offline, expired token, RPC missing because 0025 has not been applied yet —
 * must never surface as an error in a creator's face. Worst case the admin's
 * "last seen" is stale, which is a reporting problem, not a user's problem.
 *
 * Called at most once per page load here; the RPC itself only counts a NEW
 * visit after a 30-minute gap, so the count means sessions rather than tabs.
 */
export function recordVisit() {
  if (visitRecorded) return;
  visitRecorded = true;
  supabase.rpc('record_creator_visit').then(
    ({ error }) => { if (error) console.warn('[visit] not recorded', error.message); },
    (err) => console.warn('[visit] not recorded', err?.message ?? err)
  );
}
